import { registerOverlay } from '../back-button';
import Phaser from 'phaser';

/**
 * UI theme for menu, level select and HUD (design width 1080).
 * A phone shows the canvas at roughly 0.36x, so nothing readable goes below
 * 24 design px. Display type is a clean system sans, data stays mono.
 */
export const SANS =
  'Inter, "Segoe UI Variable", "Segoe UI", system-ui, -apple-system, Roboto, ' +
  '"Yu Gothic", "Microsoft YaHei", "Malgun Gothic", "Hiragino Sans", sans-serif';

export const TYPE = {
  label: 24,   // smallest readable size (eyebrows, status)
  body: 30,
  button: 32,
  title: 64,
  hero: 104,
} as const;

export const UI = {
  surface: 0x0b1226,
  surfaceHi: 0x111b36,
  hairline: 0x24305a,
  text: '#f2f6ff',
  textDim: '#8d98bd',
  textFaint: '#56608a',
  accent: 0x6a8cff,
  accentHex: '#8fb0ff',
  signal: 0x66ffaa,
  signalHex: '#88ffb0',
  hot: 0xff5db1,
  hotHex: '#ff7cc0',
  cool: 0x18e0ff,
  coolHex: '#7ef0ff',
  warm: '#ffd070',
  radius: 22,
  /** Minimum comfortable hit height in design px (about 48 CSS px on a phone). */
  hit: 132,
} as const;

export interface PanelStyle {
  fill?: number;
  fillAlpha?: number;
  stroke?: number;
  strokeAlpha?: number;
  strokeWidth?: number;
  radius?: number;
}

const PAD = 4;

/**
 * Rounded panels are baked once into a texture and drawn as an Image. A Phaser
 * Graphics object is re-tessellated every frame and is never culled, which made
 * the menu (about 20 panels) the most expensive scene. An Image is one quad and
 * is culled when off screen. Textures are cached by style, so hover states and
 * repeated sizes cost nothing after the first use.
 */
export function bakeRounded(scene: Phaser.Scene, w: number, h: number, st: Required<PanelStyle>): string {
  const key = `rr:${Math.round(w)}x${Math.round(h)}:${st.fill}:${st.fillAlpha}:${st.stroke}:${st.strokeAlpha}:${st.strokeWidth}:${st.radius}`;
  if (!scene.textures.exists(key)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(st.fill, st.fillAlpha);
    g.fillRoundedRect(PAD, PAD, w, h, st.radius);
    if (st.strokeWidth > 0) {
      g.lineStyle(st.strokeWidth, st.stroke, st.strokeAlpha);
      g.strokeRoundedRect(PAD, PAD, w, h, st.radius);
    }
    g.generateTexture(key, Math.ceil(w) + PAD * 2, Math.ceil(h) + PAD * 2);
    g.destroy();
  }
  return key;
}

/** Bake any small vector drawing into a cached texture. Returns the key. */
export function bakeDraw(scene: Phaser.Scene, key: string, w: number, h: number, draw: (g: Phaser.GameObjects.Graphics) => void): string {
  if (!scene.textures.exists(key)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    draw(g);
    g.generateTexture(key, w, h);
    g.destroy();
  }
  return key;
}

/** A padlock icon centred on the returned image's position. */
export function padlock(scene: Phaser.Scene, x: number, y: number, color: number): Phaser.GameObjects.Image {
  const key = bakeDraw(scene, `padlock:${color}`, 64, 64, (g) => {
    const ox = 32, oy = 30;
    g.lineStyle(3, color, 1);
    g.beginPath();
    g.arc(ox, oy - 8, 12, Math.PI, 0, false);
    g.strokePath();
    g.lineBetween(ox - 12, oy - 8, ox - 12, oy + 3);
    g.lineBetween(ox + 12, oy - 8, ox + 12, oy + 3);
    g.fillStyle(color, 0.18);
    g.fillRect(ox - 17, oy + 3, 34, 22);
    g.lineStyle(3, color, 1);
    g.strokeRect(ox - 17, oy + 3, 34, 22);
    g.fillStyle(color, 1);
    g.fillCircle(ox, oy + 12, 3.5);
    g.fillRect(ox - 1.5, oy + 12, 3, 9);
  });
  return scene.add.image(x, y, key);
}

/** Row of progress dots (solid when done, ring otherwise), left aligned at x, centred on y. */
export function dotRow(scene: Phaser.Scene, x: number, y: number, done: boolean[], color: number): Phaser.GameObjects.Image {
  const w = done.length * 26 + 8;
  const key = bakeDraw(scene, `dots:${color}:${done.map((d) => (d ? 1 : 0)).join('')}`, w, 24, (g) => {
    done.forEach((d, j) => {
      const cx = 12 + j * 26;
      if (d) { g.fillStyle(color, 1); g.fillCircle(cx, 12, 8); }
      else { g.lineStyle(2, color, 0.55); g.strokeCircle(cx, 12, 8); }
    });
  });
  return scene.add.image(x - 12, y, key).setOrigin(0, 0.5);
}

/** A rounded flat panel that can restyle itself (used for hover and press). */
export class Panel {
  /** Display object: add it to a container, scale it, tween it. */
  readonly gfx: Phaser.GameObjects.Image;
  private st: Required<PanelStyle>;
  constructor(
    private scene: Phaser.Scene,
    x: number, y: number, private w: number, private h: number,
    style: PanelStyle = {},
  ) {
    this.st = {
      fill: UI.surface, fillAlpha: 0.92, stroke: UI.hairline, strokeAlpha: 1,
      strokeWidth: 2, radius: UI.radius, ...style,
    };
    // Centre origin so tweens can scale it cleanly.
    this.gfx = scene.add.image(x + w / 2, y + h / 2, bakeRounded(scene, w, h, this.st));
  }
  restyle(patch: PanelStyle) {
    this.st = { ...this.st, ...patch };
    this.gfx.setTexture(bakeRounded(this.scene, this.w, this.h, this.st));
  }
}

/** Soft press feedback: a quick scale dip on any game object. */
export function pressFx(scene: Phaser.Scene, target: Phaser.GameObjects.GameObject) {
  scene.tweens.add({
    targets: target, scale: { from: 0.97, to: 1 }, duration: 140, ease: 'Cubic.easeOut',
  });
}

/** Strip the old decorative arrows from a string so every language reads clean. */
export const clean = (s: string): string =>
  s.replace(/[›‹▸◂▾▴]/g, '').replace(/\s{2,}/g, ' ').trim();

/** True when the player asked their system for less motion. */
export function reducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export interface ButtonOpts { primary?: boolean; size?: number }

/** A rounded button. Primary is solid colour with dark text, otherwise outlined. */
export function uiButton(
  scene: Phaser.Scene, x: number, y: number, w: number, h: number,
  label: string, color: number, onClick: () => void, opts: ButtonOpts = {},
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const primary = !!opts.primary;
  const style = (hover: boolean): Required<PanelStyle> => primary
    ? { fill: color, fillAlpha: hover ? 1 : 0.92, stroke: color, strokeAlpha: 1, strokeWidth: 0, radius: UI.radius }
    : { fill: hover ? UI.surfaceHi : UI.surface, fillAlpha: 1, stroke: color, strokeAlpha: hover ? 1 : 0.7, strokeWidth: 2, radius: UI.radius };
  const g = scene.add.image(0, 0, bakeRounded(scene, w, h, style(false)));
  const draw = (hover: boolean) => g.setTexture(bakeRounded(scene, w, h, style(hover)));
  const txt = scene.add.text(0, 0, clean(label), {
    fontFamily: SANS, fontSize: `${opts.size ?? TYPE.button}px`,
    color: primary ? '#05060d' : '#f2f6ff', fontStyle: '800',
  }).setOrigin(0.5);
  // Long translations shrink to fit instead of spilling out of the button.
  if (txt.width > w - 40) txt.setScale((w - 40) / txt.width);
  const hit = scene.add.rectangle(0, 0, w, h, 0xffffff, 0).setInteractive({ useHandCursor: true });
  c.add([g, txt, hit]);
  hit.on('pointerover', () => draw(true));
  hit.on('pointerout', () => draw(false));
  hit.on('pointerdown', () => { pressFx(scene, c); onClick(); });
  return c;
}

export interface Modal {
  root: Phaser.GameObjects.Container;
  dim: Phaser.GameObjects.Rectangle;
  cx: number; cy: number; w: number; h: number;
  left: number; right: number; top: number;
}

/** Centred rounded sheet over a dim layer. Content is added to `root`. */
export function openModal(
  scene: Phaser.Scene,
  o: { title: string; accent: number; accentHex: string; h: number; depth?: number },
): Modal {
  const { width, height } = scene.scale;
  const root = scene.add.container(0, 0).setDepth(o.depth ?? 3000).setScrollFactor(0);
  const dim = scene.add.rectangle(0, 0, width, height, 0x000000, 0.78)
    .setOrigin(0).setScrollFactor(0).setInteractive();
  const w = Math.min(width - 96, 900);
  const h = Math.min(o.h, height - 120);
  const cx = width / 2, cy = height / 2;
  const panel = new Panel(scene, cx - w / 2, cy - h / 2, w, h, {
    fill: 0x0b1226, fillAlpha: 0.99, stroke: o.accent, strokeAlpha: 0.85,
  });
  const title = scene.add.text(cx, cy - h / 2 + 70, o.title, {
    fontFamily: SANS, fontSize: '46px', color: o.accentHex, fontStyle: '800',
  }).setOrigin(0.5);
  if (title.width > w - 96) title.setScale((w - 96) / title.width);
  root.add([dim, panel.gfx, title]);
  if (!reducedMotion()) {
    root.setAlpha(0);
    panel.gfx.setScale(0.96);
    scene.tweens.add({ targets: root, alpha: 1, duration: 160, ease: 'Cubic.easeOut' });
    scene.tweens.add({ targets: panel.gfx, scale: 1, duration: 220, ease: 'Cubic.easeOut' });
  }
  registerOverlay(() => !!root.scene && root.active, () => dim.emit('pointerup'));
  return { root, dim, cx, cy, w, h, left: cx - w / 2 + 48, right: cx + w / 2 - 48, top: cy - h / 2 + 140 };
}

/**
 * Smallest font a phone can read. The canvas is 1080 design px wide and a 390
 * css px phone shows it at 0.36x, so 30 design px is about 10.8 css px. Any
 * text asked for below that is raised to it, so a stray 18 or 24 px literal
 * cannot ship as 6 to 9 css px type. The node label font stack (node labels, the credit footer) is
 * exempt: node labels scale with the board camera and the footer is not content.
 */
export const MIN_FONT_PX = 30;

export function installFontFloor(): void {
  const proto = Phaser.GameObjects.GameObjectFactory.prototype as unknown as {
    text: (x: number, y: number, text: string | string[], style?: Phaser.Types.GameObjects.Text.TextStyle) => Phaser.GameObjects.Text;
    __floored?: boolean;
  };
  if (proto.__floored) return;
  proto.__floored = true;
  const orig = proto.text;
  proto.text = function (this: unknown, x, y, text, style) {
    if (style && style.fontSize !== undefined) {
      const px = parseFloat(String(style.fontSize));
      const mono = String(style.fontFamily ?? "").startsWith("ui-monospace, SFMono-Regular, Menlo, monospace");
      if (!mono && px >= 16 && px < MIN_FONT_PX) style = { ...style, fontSize: `${MIN_FONT_PX}px` };
    }
    return orig.call(this, x, y, text, style);
  };
}
